/**
 * These functions work out the positions of the interstitial piece based on the current limited
 * approach Dora is taking around the interstitial armrest UI.
 */
import { ModaSofa } from '@dora/backpack';
import * as O from 'fp-ts/Option';

/**
 * Place the interstitial for a sofas left arm. Applicable to all but straight sofas.
 *
 * the left interstitial goes in the first available position.
 */
export const leftArmInterstitialPosition = (size: ModaSofa.Size.Size): O.Option<number> => {
    switch (size.shape) {
        case 'straight':
            return O.none;
    }

    // the first spot for an interstitial is always the first slot after the endpiece
    return O.some(0);
};

/**
 * Place the interstitials for a sofas middle arm. Applicable to all but the corner sofa.
 * This is considered the only arm for the straight sofa
 *
 * The middle interstitial armrests go around the middle of the sofa, separated by
 * at least two seats from each other, as symmetrically as possible. For an odd
 * number of armrests with an odd number of seats, the middle armrest will fall to the
 * left (e.g resulting in a spacing of 3 seats between the middle and right armrests if
 * there are three). For an even number of arm rests and odd number of seats, they are
 * placed symmetrically about the middle with three seats between the middle armrests.
 */
export const middleArmInterstitialPositions = (
    size: ModaSofa.Size.Size,
    interstitials: number,
): Array<number> => {
    const armStart = size.shape === 'u-shape' ? size.left + 1 : 0;

    return size.shape === 'straight' || size.shape === 'u-shape'
        ? new Array(interstitials)
              .fill(0)
              .map(
                  (_, i) =>
                      armStart +
                      Math.floor(size.middle / 2) +
                      1 -
                      interstitials +
                      (interstitials > 1
                          ? Math.floor(i * (2 + (size.middle % 2) / (interstitials - 1)))
                          : 0),
              )
        : [];
};

/**
 * Place the interstitial for a sofas right arm. Applicable to all but the straight sofa.
 *
 * The right interstitial armrest is placed as far right as possible, just on the inside of the
 * right endpiece.
 */
export const rightArmInterstitialPosition = (size: ModaSofa.Size.Size): O.Option<number> => {
    // the right interstitial piece needs to be the last slot in the sofa. This requires that we add
    // up the other pieces, with one extra for every corner piece
    switch (size.shape) {
        case 'corner':
            return O.some(size.left + size.right + 1);
        case 'u-shape':
            return O.some(size.left + size.middle + size.right + 2);
        default:
            // not every sofa has a right piece
            return O.none;
    }
};
